<template>
  <div class="display-image-gallery">
    <div v-if="isLoading" class="display-image-gallery__empty">
      <i class="bi bi-image"></i>
      <span>Loading display images…</span>
    </div>

    <div v-else-if="!images.length" class="display-image-gallery__empty">
      <i class="bi bi-images"></i>
      <span>No display images selected yet.</span>
      <small>Mark PNG or JPG files in the files panel to show them here.</small>
    </div>

    <div v-else class="display-image-gallery__body">
      <div class="display-image-gallery__stage">
        <div v-if="allowManage && currentImage" class="display-image-gallery__stage-actions">
          <button
            v-if="images.length > 1 && activeIndex !== 0"
            type="button"
            class="display-image-gallery__ghost-btn"
            :disabled="isMutating"
            @click="makeCurrentMainImage"
          >
            Make Main Picture
          </button>
          <button
            type="button"
            class="display-image-gallery__ghost-btn display-image-gallery__ghost-btn--danger"
            :disabled="isMutating"
            @click="unmarkImage(currentImage)"
          >
            Remove
          </button>
        </div>

        <button
          v-if="images.length > 1"
          type="button"
          class="display-image-gallery__nav display-image-gallery__nav--left"
          aria-label="Previous image"
          @click="goToPrevious"
        >
          <i class="bi bi-chevron-left"></i>
        </button>

        <img
          :src="currentImageUrl"
          :alt="currentImageName"
          class="display-image-gallery__image"
          role="button"
          tabindex="0"
          @click="openImageOverlay"
          @keydown.enter.prevent="openImageOverlay"
          @keydown.space.prevent="openImageOverlay"
        >

        <button
          v-if="images.length > 1"
          type="button"
          class="display-image-gallery__nav display-image-gallery__nav--right"
          aria-label="Next image"
          @click="goToNext"
        >
          <i class="bi bi-chevron-right"></i>
        </button>
      </div>
    </div>
  </div>

  <Teleport to="body">
    <div
      v-if="isOverlayOpen && currentImageUrl"
      class="display-image-gallery__overlay"
      @click.self="closeImageOverlay"
    >
      <div class="display-image-gallery__overlay-modal">
        <div class="display-image-gallery__overlay-header">
          <h3>{{ currentImageName }}</h3>
          <button
            type="button"
            class="display-image-gallery__overlay-close"
            @click="closeImageOverlay"
          >
            Close
          </button>
        </div>

        <div class="display-image-gallery__overlay-body">
          <button
            v-if="images.length > 1"
            type="button"
            class="display-image-gallery__nav display-image-gallery__nav--overlay-left"
            aria-label="Previous image"
            @click="goToPrevious"
          >
            <i class="bi bi-chevron-left"></i>
          </button>

          <img
            :src="currentImageUrl"
            :alt="currentImageName"
            class="display-image-gallery__overlay-image"
          >

          <button
            v-if="images.length > 1"
            type="button"
            class="display-image-gallery__nav display-image-gallery__nav--overlay-right"
            aria-label="Next image"
            @click="goToNext"
          >
            <i class="bi bi-chevron-right"></i>
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
import apiService from '@/services/apiService';
import { mapState } from 'vuex';
import { useToast } from 'vue-toastification';
import {
  DISPLAY_IMAGE_UPDATED_EVENT,
  buildDisplayImageUrl,
  emitDisplayImagesUpdated,
  getDisplayImageOrder,
  getFileNameFromPath,
  matchesDisplayImageEntity,
} from '@/utils/displayImages';

export default {
  name: 'DisplayImageGallery',
  props: {
    entityType: {
      type: String,
      required: true,
    },
    entityId: {
      type: [Number, String],
      required: true,
    },
    allowManage: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      images: [],
      activeIndex: 0,
      isLoading: false,
      isMutating: false,
      isOverlayOpen: false,
    };
  },
  computed: {
    ...mapState(['loggedInUser']),
    normalizedEntityId() {
      return Number(this.entityId);
    },
    currentImage() {
      return this.images[this.activeIndex] || null;
    },
    currentImageUrl() {
      return this.getImageUrl(this.currentImage);
    },
    currentImageName() {
      return this.getImageName(this.currentImage);
    },
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  watch: {
    entityType: {
      immediate: true,
      handler() {
        this.fetchDisplayImages();
      },
    },
    entityId() {
      this.fetchDisplayImages();
    },
  },
  mounted() {
    window.addEventListener(DISPLAY_IMAGE_UPDATED_EVENT, this.handleExternalUpdate);
    window.addEventListener('keydown', this.handleGlobalKeydown);
  },
  beforeUnmount() {
    window.removeEventListener(DISPLAY_IMAGE_UPDATED_EVENT, this.handleExternalUpdate);
    window.removeEventListener('keydown', this.handleGlobalKeydown);
  },
  methods: {
    handleGlobalKeydown(event) {
      if (event.key === 'Escape' && this.isOverlayOpen) {
        this.closeImageOverlay();
      }
    },
    handleExternalUpdate(event) {
      if (matchesDisplayImageEntity(event?.detail, this.entityType, this.entityId)) {
        this.fetchDisplayImages(false);
      }
    },
    openImageOverlay() {
      if (!this.currentImageUrl) return;
      this.isOverlayOpen = true;
    },
    closeImageOverlay() {
      this.isOverlayOpen = false;
    },
    async fetchDisplayImages(resetActive = true) {
      if (!this.entityType || !this.entityId || !this.loggedInUser?.entra_id) {
        this.images = [];
        this.isOverlayOpen = false;
        return;
      }

      this.isLoading = true;
      try {
        const response = await apiService.getEntityDisplayImages(
          this.entityType,
          this.entityId,
          this.loggedInUser.entra_id
        );
        const images = Array.isArray(response?.display_images)
          ? response.display_images
          : Array.isArray(response)
            ? response
            : [];

        this.images = [...images].sort((left, right) => {
          return this.getImageOrder(left, 0) - this.getImageOrder(right, 0);
        });

        if (resetActive) {
          this.activeIndex = 0;
        } else if (this.activeIndex >= this.images.length) {
          this.activeIndex = Math.max(this.images.length - 1, 0);
        }
      } catch (error) {
        if (error?.response?.status === 404) {
          this.images = [];
          this.activeIndex = 0;
          this.isOverlayOpen = false;
        } else {
          console.error('Failed to fetch display images:', error);
          this.toast.error('Failed to load display images.');
        }
      } finally {
        this.isLoading = false;
      }
    },
    getImageUrl(image) {
      if (!image?.file_id || !this.loggedInUser?.entra_id) return '';
      return buildDisplayImageUrl(image.file_id, this.loggedInUser.entra_id);
    },
    getImageName(image) {
      return getFileNameFromPath(image?.path);
    },
    getImageOrder(image, index) {
      return getDisplayImageOrder(image, index);
    },
    goToPrevious() {
      if (!this.images.length) return;
      this.activeIndex = this.activeIndex === 0 ? this.images.length - 1 : this.activeIndex - 1;
    },
    goToNext() {
      if (!this.images.length) return;
      this.activeIndex = this.activeIndex >= this.images.length - 1 ? 0 : this.activeIndex + 1;
    },
    async makeCurrentMainImage() {
      if (this.isMutating || !this.currentImage || this.activeIndex === 0) return;

      const reordered = [...this.images];
      const [currentImage] = reordered.splice(this.activeIndex, 1);
      reordered.unshift(currentImage);

      this.isMutating = true;
      try {
        await apiService.reorderEntityDisplayImages(
          this.entityType,
          this.entityId,
          reordered.map((image) => image.file_id),
          this.loggedInUser.entra_id
        );
        this.images = reordered;
        this.activeIndex = 0;
        emitDisplayImagesUpdated(this.entityType, this.entityId);
      } catch (error) {
        console.error('Failed to update main display image:', error);
        this.toast.error('Failed to update main display image.');
        await this.fetchDisplayImages(false);
      } finally {
        this.isMutating = false;
      }
    },
    async moveImage(direction) {
      if (this.isMutating || !this.currentImage) return;

      const targetIndex = this.activeIndex + direction;
      if (targetIndex < 0 || targetIndex >= this.images.length) return;

      const reordered = [...this.images];
      const [movedImage] = reordered.splice(this.activeIndex, 1);
      reordered.splice(targetIndex, 0, movedImage);

      this.isMutating = true;
      try {
        await apiService.reorderEntityDisplayImages(
          this.entityType,
          this.entityId,
          reordered.map((image) => image.file_id),
          this.loggedInUser.entra_id
        );
        this.images = reordered;
        this.activeIndex = targetIndex;
        emitDisplayImagesUpdated(this.entityType, this.entityId);
      } catch (error) {
        console.error('Failed to reorder display images:', error);
        this.toast.error('Failed to reorder display images.');
        await this.fetchDisplayImages(false);
      } finally {
        this.isMutating = false;
      }
    },
    async unmarkImage(image) {
      if (!image?.file_id || this.isMutating) return;

      this.isMutating = true;
      try {
        await apiService.unmarkDisplayImage(image.file_id, this.loggedInUser.entra_id);
        const nextImages = this.images.filter((entry) => entry.file_id !== image.file_id);
        this.images = nextImages;
        this.activeIndex = Math.min(this.activeIndex, Math.max(nextImages.length - 1, 0));
        if (!nextImages.length) {
          this.isOverlayOpen = false;
        }
        emitDisplayImagesUpdated(this.entityType, this.entityId);
      } catch (error) {
        console.error('Failed to remove display image:', error);
        this.toast.error('Failed to remove display image.');
      } finally {
        this.isMutating = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/main.scss";

.display-image-gallery {
  background: $secondary-color;
  border: 1px solid rgba($primary-color, 0.12);
  border-radius: 18px;
  box-shadow: 0 16px 34px rgba(15, 23, 42, 0.08);
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-height: 100%;
}

.display-image-gallery__ghost-btn,
.display-image-gallery__icon-btn {
  border: 1px solid rgba($primary-color, 0.16);
  background: rgba(#fff, 0.94);
  color: $primary-color;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.display-image-gallery__ghost-btn {
  padding: 8px 12px;
  font-weight: 600;
}

.display-image-gallery__ghost-btn--danger {
  border-color: rgba(#b42318, 0.22);
  color: #b42318;
}

.display-image-gallery__icon-btn {
  width: 36px;
  height: 36px;
}

.display-image-gallery__ghost-btn:hover,
.display-image-gallery__icon-btn:hover,
.display-image-gallery__thumb:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 24px rgba($primary-color, 0.12);
}

.display-image-gallery__empty {
  min-height: 320px;
  border-radius: 14px;
  border: 1px dashed rgba($primary-color, 0.22);
  background: rgba($primary-color, 0.03);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 10px;
  color: rgba($primary-color, 0.72);
  text-align: center;
  padding: 24px;

  i {
    font-size: 2rem;
  }

  small {
    max-width: 28ch;
  }
}

.display-image-gallery__body {
  display: flex;
  flex-direction: column;
}

.display-image-gallery__stage {
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  background: rgba($primary-color, 0.04);
  min-height: 320px;
  padding: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba($primary-color, 0.08);
}

.display-image-gallery__stage-actions {
  position: absolute;
  top: 14px;
  right: 14px;
  z-index: 2;
  display: inline-flex;
  gap: 8px;
  opacity: 0;
  pointer-events: none;
  transform: translateY(-4px);
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.display-image-gallery__stage:hover .display-image-gallery__stage-actions,
.display-image-gallery__stage:focus-within .display-image-gallery__stage-actions {
  opacity: 1;
  pointer-events: auto;
  transform: translateY(0);
}

.display-image-gallery__image {
  display: block;
  max-width: 100%;
  width: auto;
  max-height: 320px;
  height: auto;
  object-fit: contain;
  cursor: zoom-in;
}

.display-image-gallery__nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 42px;
  height: 42px;
  border: none;
  border-radius: 999px;
  background: rgba(24, 57, 43, 0.76);
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 1;
}

.display-image-gallery__nav--left {
  left: 14px;
}

.display-image-gallery__nav--right {
  right: 14px;
}

button:disabled {
  cursor: not-allowed;
  opacity: 0.55;
  transform: none;
  box-shadow: none;
}

.display-image-gallery__overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(0, 0, 0, 0.72);
}

.display-image-gallery__overlay-modal {
  width: min(1200px, calc(100vw - 48px));
  height: calc(100vh - 48px);
  display: flex;
  flex-direction: column;
  border-radius: 18px;
  overflow: hidden;
  background: rgba(#0f172a, 0.96);
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.35);
}

.display-image-gallery__overlay-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 20px;
  background: rgba(#111827, 0.94);
  color: #fff;

  h3 {
    margin: 0;
    font-size: 1rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.display-image-gallery__overlay-close {
  border: none;
  border-radius: 10px;
  padding: 8px 14px;
  background: rgba(#fff, 0.12);
  color: #fff;
  cursor: pointer;
}

.display-image-gallery__overlay-body {
  position: relative;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 72px;
}

.display-image-gallery__overlay-image {
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
}

.display-image-gallery__nav--overlay-left {
  left: 18px;
}

.display-image-gallery__nav--overlay-right {
  right: 18px;
}

@media (max-width: 768px) {
  .display-image-gallery__overlay {
    padding: 12px;
  }

  .display-image-gallery__overlay-modal {
    width: calc(100vw - 24px);
    height: calc(100vh - 24px);
  }

  .display-image-gallery__overlay-body {
    padding: 16px 56px;
  }
}
</style>
